import {
    createContext,
    useContext,
    type ComponentPropsWithRef,
    type ReactNode,
} from 'react'
import { Icon, IconNameEnum } from '@/React/Shared/Component/Icon'

export type AlertType = 'success' | 'info' | 'warning' | 'danger'

interface AlertContextValue {
    type: AlertType
}

const AlertContext = createContext<AlertContextValue>({
    type: 'info',
})

export interface AlertContainerProps extends Omit<
    ComponentPropsWithRef<'div'>,
    'title'
> {
    type: AlertType
    children?: ReactNode
}

interface AlertStyleConfig {
    containerClass: string
    iconName: IconNameEnum
    iconClass: string
}

const alertStyles: Record<AlertType, AlertStyleConfig> = {
    success: {
        containerClass:
            'p-3 bg-green-50 border border-green-600 text-green-900 rounded-lg2',
        iconName: IconNameEnum.CircleCheck,
        iconClass: 'w-4 h-4 text-green-900',
    },
    info: {
        containerClass:
            'p-3 bg-blue-50 border border-blue-500 text-blue-900 rounded-lg2',
        iconName: IconNameEnum.InfoCircle,
        iconClass: 'w-4 h-4 text-blue-900',
    },
    warning: {
        containerClass:
            'p-3 bg-yellow-50 border border-yellow-500 text-yellow-900 rounded-lg2',
        iconName: IconNameEnum.Alert,
        iconClass: 'w-4 h-4 text-yellow-900',
    },
    danger: {
        containerClass:
            'p-3 bg-red-50 border border-red-600 text-red-900 rounded-lg2',
        iconName: IconNameEnum.Alert,
        iconClass: 'w-4 h-4 text-red-900',
    },
}

export function AlertContainer({
    type,
    children,
    className = '',
    ...restProps
}: AlertContainerProps) {
    const config = alertStyles[type] ?? alertStyles.info

    return (
        <AlertContext.Provider value={{ type }}>
            <div
                role={
                    restProps.role ??
                    (type === 'danger' || type === 'warning'
                        ? 'alert'
                        : 'status')
                }
                {...restProps}
                className={`text-sm ${className}`.trim()}
            >
                <div className={config.containerClass}>{children}</div>
            </div>
        </AlertContext.Provider>
    )
}

export interface AlertTitleProps extends ComponentPropsWithRef<'div'> {
    iconName?: IconNameEnum
    iconClass?: string
    children: ReactNode
}

export function AlertTitle({
    iconName,
    iconClass,
    children,
    className = '',
    ...restProps
}: AlertTitleProps) {
    const context = useContext(AlertContext)
    const config = alertStyles[context.type] ?? alertStyles.info
    const resolvedIconName = iconName ?? config.iconName
    const resolvedIconClass = iconClass ?? config.iconClass

    return (
        <div
            {...restProps}
            className={`flex items-center justify-start gap-2 ${className}`.trim()}
        >
            {resolvedIconName ? (
                <Icon
                    name={resolvedIconName}
                    className={resolvedIconClass}
                    aria-hidden="true"
                />
            ) : null}
            <span className="font-semibold">{children}</span>
        </div>
    )
}

export function AlertContent({
    children,
    className = '',
    ...restProps
}: ComponentPropsWithRef<'div'>) {
    return (
        <div
            {...restProps}
            className={`mt-2 ${className}`.trim()}
        >
            {children}
        </div>
    )
}

export const Alert = Object.assign(AlertContainer, {
    Container: AlertContainer,
    Title: AlertTitle,
    Content: AlertContent,
})
